/**
 * API client for the Django backend, which lives on its own host.
 *
 * - Talks to `VITE_API_URL` (default http://localhost:8000) with credentials,
 *   so the session cookie travels cross-origin. app.<domain> and api.<domain>
 *   are same-site, which is what makes SameSite=Lax cookies work here.
 * - Holds the CSRF token in memory (the SPA cannot read the API's cookies),
 *   fetching it lazily and refreshing it once if the server rejects it.
 * - Throws ApiError with the parsed body on non-2xx responses.
 */

export const API_URL = (import.meta.env.VITE_API_URL ?? "http://localhost:8000").replace(/\/$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(describeError(status, body));
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Field errors as DRF returns them: { field: ["msg"] } or { detail: "msg" }. */
  get fieldErrors(): Record<string, string[]> {
    return extractFieldErrors(this.body);
  }
}

function extractFieldErrors(body: unknown): Record<string, string[]> {
  if (typeof body !== "object" || body === null) return {};
  const out: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
    if (Array.isArray(value)) out[key] = value.map(String);
    else if (typeof value === "string") out[key] = [value];
  }
  return out;
}

/** Best single human-readable message: detail, then non-field errors, then the first field. */
function describeError(status: number, body: unknown): string {
  const errors = extractFieldErrors(body);
  const first = errors.detail ?? errors.non_field_errors ?? Object.values(errors)[0];
  return first?.[0] ?? `Request failed (${status})`;
}

function isCsrfFailure(error: ApiError): boolean {
  return error.status === 403 && /csrf/i.test(error.fieldErrors.detail?.[0] ?? "");
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

let csrfToken: string | null = null;
let csrfRequest: Promise<string> | null = null;

async function fetchCsrfToken(): Promise<string> {
  const response = await fetch(`${API_URL}/api/auth/csrf/`, { credentials: "include" });
  if (!response.ok) throw new ApiError(response.status, await response.text());
  const body = (await response.json()) as { csrfToken: string };
  return body.csrfToken;
}

/** Return the CSRF token, fetching it once and sharing concurrent callers. */
export async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  csrfRequest ??= fetchCsrfToken()
    .then((token) => {
      csrfToken = token;
      return token;
    })
    .finally(() => {
      csrfRequest = null;
    });
  return csrfRequest;
}

/** Django rotates the CSRF token on login and logout; call this afterwards. */
export function invalidateCsrfToken(): void {
  csrfToken = null;
}

export interface ApiRequestInit extends Omit<RequestInit, "body"> {
  /** JSON-serialisable body. Pass FormData for uploads. */
  body?: unknown;
}

async function send<T>(path: string, init: ApiRequestInit, method: string): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");

  let body: BodyInit | undefined;
  if (init.body instanceof FormData) {
    body = init.body;
  } else if (init.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.body);
  }

  if (!SAFE_METHODS.has(method)) {
    headers.set("X-CSRFToken", await ensureCsrfToken());
  }

  const response = await fetch(`${API_URL}/api${path}`, {
    ...init,
    method,
    headers,
    body,
    credentials: "include",
  });

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const payload: unknown =
    response.status === 204 ? undefined : isJson ? await response.json() : await response.text();

  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

export async function api<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  try {
    return await send<T>(path, init, method);
  } catch (error) {
    // A stale token (e.g. after login rotated it) gets one refresh-and-retry.
    if (error instanceof ApiError && isCsrfFailure(error) && !SAFE_METHODS.has(method)) {
      invalidateCsrfToken();
      return send<T>(path, init, method);
    }
    throw error;
  }
}
